// A20 W7a — one ticket, with the session that produced it.
//
// This is the URL github-issues.service.ts puts in every issue body
// (`${replayBaseUrl}/support/tickets/${t.id}`), so the route shape is a
// contract, not a preference.
//
// THREE THINGS THIS PAGE REFUSES TO FUDGE:
//
//   1. Masked vs revealed is stated before the play button, in a banner, in
//      both the header and above the stage. A reviewer must never have to guess
//      whether the words on screen are a customer's real data.
//   2. A ticket that never reached GitHub looks stuck — the sync state and its
//      recorded error are rendered, not hidden behind a successful-looking
//      absence of a link.
//   3. The recording read is a separate request with its own outcome. A ticket
//      whose bytes cannot be fetched still shows everything else, with the
//      reason stated. That is the "partial" state of the eight, which per
//      StateFrame's own header is rendered inline rather than as a takeover.
//
// ── A20 PHASE 1b: THIS PAGE STOPPED BEING READ-ONLY ─────────────────────────
// Migration 41 gave tickets a lifecycle — a validated state machine, an
// append-only audit trail, and an explicit GitHub escalation — and none of it
// had a caller. The status was a pill you could not click, `support_ticket_
// events` was written and never read, and "escalate from the console after
// triage" named an action that did not exist anywhere. Three components close
// that: StatusControl and GithubCard (./TicketActions) and TicketTimeline.
//
// They are separate cards rather than a toolbar because they are separate
// decisions with different consequences — one arms a deletion clock, one
// publishes customer correspondence — and a row of buttons flattens that.
// Every mutation re-reads BOTH the ticket and the timeline through `reload`
// below: `allowed_transitions` is computed from the status the server holds, so
// a control that did not re-read would keep offering the moves that were legal
// before the click.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft, Film, RefreshCw } from 'lucide-react';
import { formatDateTime } from '@/lib/utils';
import { ApiError } from '@/lib/supabase';
import { Pill } from '@/components/shared/badges';
import { Card, CardHead } from '@/components/shared/primitives';
import { StateFrame, classifyApiError, type LoadState } from '@/components/shared/StateFrame';
import { Skel } from '@/components/shared/SkeletonLoader';
import { GithubSyncChip, MaskBanner, MaskPill, StatusPill, readMaskEvidence, type MaskEvidence } from './chips';
import { GithubCard, StatusControl } from './TicketActions';
import { TicketTimeline } from './TicketTimeline';
import { SupportBundleCard } from './SupportBundleCard';
import { ReplayPlayer } from './ReplayPlayer';
import {
  getTicket, fetchRecordingEvents, severityTint, sourceLabel, surfaceLabel,
  formatDuration, formatBytes, MASKING_VERDICT_LABEL, type SupportTicketDetail,
} from './api';

const btnSec: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
  borderRadius: 8, background: 'var(--surface-raised)', color: 'var(--text-primary)',
  border: '1px solid var(--border)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
};

/** Recording fetch is its own little state machine — it fails independently. */
type ReplayState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; events: unknown[] }
  | { kind: 'absent' }
  | { kind: 'error'; message: string };

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: 3 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-primary)', wordBreak: 'break-word' }}>{children}</div>
    </div>
  );
}

const dash = <span style={{ color: 'var(--text-muted)' }}>—</span>;

export function TicketDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<SupportTicketDetail | null>(null);
  const [state, setState] = useState<LoadState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [replay, setReplay] = useState<ReplayState>({ kind: 'idle' });
  // Bumped after every committed mutation so the timeline re-reads itself. A
  // counter rather than a callback ref because the timeline owns its own load,
  // its own error and its own retry — this page must not learn how to read it.
  const [timelineKey, setTimelineKey] = useState(0);

  const load = useCallback(async () => {
    setState('loading');
    setErrorMessage(null);
    setReplay({ kind: 'idle' });
    try {
      setTicket(await getTicket(id));
      setState('ready');
    } catch (err) {
      setTicket(null);
      setState(classifyApiError(err));
      setErrorMessage(err instanceof Error ? err.message : 'Failed to load');
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  /**
   * What a committed mutation does to this page.
   *
   * A re-read, NOT a local patch of the ticket in state. The response of
   * set_ticket_status() carries the new status, but `allowed_transitions`,
   * `purge_after` and the escalation stamps are all recomputed server-side (two
   * of them by migration 36's trigger), so patching a field here would leave the
   * rest of the page describing the ticket as it was a moment ago. Re-reading
   * costs one request and cannot go stale.
   */
  const reload = useCallback(() => {
    load();
    setTimelineKey((n) => n + 1);
  }, [load]);

  const loadReplay = useCallback(async () => {
    setReplay({ kind: 'loading' });
    try {
      setReplay({ kind: 'ready', events: await fetchRecordingEvents(id) });
    } catch (err) {
      // 404/410 mean "the bytes are not there" — a fact about this ticket, not
      // a malfunction. Everything else is a real failure and says so.
      if (err instanceof ApiError && (err.status === 404 || err.status === 410)) {
        setReplay({ kind: 'absent' });
        return;
      }
      setReplay({ kind: 'error', message: err instanceof Error ? err.message : 'Could not load the recording.' });
    }
  }, [id]);

  const skeleton = (
    <div style={{ display: 'grid', gap: 16 }}>
      <Skel height={92} borderRadius={12} />
      <Skel height={220} borderRadius={12} />
      <Skel height={320} borderRadius={12} />
    </div>
  );

  const sev = ticket ? severityTint(ticket.severity) : { bg: '', fg: '' };

  // ── ONE evidence object for the whole page ────────────────────────────────
  // The banner, the queue-style pill and the pre-play caption all describe the
  // same two facts (the reporter's claim, the server's verdict). Built once so
  // they cannot drift into telling three different stories about one ticket.
  const maskEvidence: MaskEvidence = {
    masked: ticket?.masked ?? true,
    verdict: ticket?.masking_verdict,
    sampled: ticket?.masking_sampled,
    legible: ticket?.masking_legible,
    truncated: ticket?.masking_truncated,
    hasCapture: ticket?.capture_available ?? false,
  };
  const maskReading = readMaskEvidence(maskEvidence);

  const client = ticket?.client_json ?? null;
  const consoleLines = ticket?.console_json ?? [];
  const networkLines = ticket?.network_json ?? [];

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <button type="button" onClick={() => navigate('/support/tickets')} style={{ ...btnSec, padding: '7px 9px' }} title="Back to triage">
            <ArrowLeft size={15} />
          </button>
          <div>
            <h1 className="page-title">{ticket?.ticket_ref ?? 'Report'}</h1>
            <p className="page-subtitle">{ticket?.title ?? 'Loading the report…'}</p>
          </div>
        </div>
        <button onClick={load} style={btnSec}><RefreshCw size={13} /> Refresh</button>
      </div>

      <StateFrame state={state} skeleton={skeleton} onRetry={load} errorMessage={errorMessage}>
        {ticket && (
          <div style={{ display: 'grid', gap: 16 }}>
            {/* Mask state first: it governs how everything below may be read. */}
            <MaskBanner {...maskEvidence} />

            <Card>
              <CardHead
                title="Report"
                sub={`${surfaceLabel(ticket.surface_key)} · ${sourceLabel(ticket.source)} · filed ${formatDateTime(ticket.created_at)}`}
                right={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Pill bg={sev.bg} fg={sev.fg}>{ticket.severity}</Pill>
                    <StatusPill status={ticket.status} />
                  </div>
                }
              />
              <div style={{ padding: '16px 18px', display: 'grid', gap: 16 }}>
                <div style={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>
                  {ticket.body}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
                  <Field label="Route">
                    {ticket.route ? <span style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{ticket.route}</span> : dash}
                  </Field>
                  <Field label="Version">{ticket.product_version || dash}</Field>
                  <Field label="Reporter">
                    {/* POST /reports/mine derives organization_id from the verified
                        session and this console deliberately asserts no identity
                        in the payload, so these are commonly null. Saying "not
                        recorded" is the honest rendering of that. */}
                    {ticket.reporter_email || ticket.reporter_user_id || (
                      <span style={{ color: 'var(--text-muted)' }}>Not recorded</span>
                    )}
                  </Field>
                  <Field label="Role">{ticket.reporter_role || dash}</Field>
                  <Field label="Org">
                    {ticket.external_org_id
                      ? <span style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{ticket.external_org_id}</span>
                      : dash}
                  </Field>
                  <Field label="Install">
                    {ticket.instance_id
                      ? <span style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{ticket.instance_id}</span>
                      : dash}
                  </Field>
                  <Field label="Locale">{ticket.locale || dash}</Field>
                  {/* The FACT, with its provenance. Deliberately a separate
                      field from the banner's headline: a triager arguing about
                      a ticket needs to be able to say WHEN it was checked and
                      over how much, not just what the answer was. */}
                  <Field label="Masking check">
                    {ticket.masking_verdict ? (
                      <span>
                        {MASKING_VERDICT_LABEL[ticket.masking_verdict] ?? ticket.masking_verdict}
                        {typeof ticket.masking_sampled === 'number' && (
                          <span style={{ color: 'var(--text-secondary)' }}>
                            {' '}· {ticket.masking_sampled} sampled
                            {ticket.masking_legible ? `, ${ticket.masking_legible} legible` : ''}
                            {ticket.masking_truncated ? ', truncated' : ''}
                          </span>
                        )}
                        {ticket.masking_checked_at && (
                          <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                            {formatDateTime(ticket.masking_checked_at)}
                          </div>
                        )}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-muted)' }}>
                        {ticket.capture_available ? 'Not checked' : 'No capture to check'}
                      </span>
                    )}
                  </Field>
                  <Field label="GitHub">
                    {/* The chip only. The FILING — its state, its retry budget,
                        the issue's own lifecycle and the escalation — has its
                        own card below, because two of those are actions. */}
                    <GithubSyncChip
                      state={ticket.github_sync_state}
                      url={ticket.github_issue_url}
                      repo={ticket.github_repo}
                      issueNumber={ticket.github_issue_number}
                      error={ticket.github_sync_error}
                      createdAt={ticket.created_at}
                      verbose
                    />
                  </Field>
                </div>
              </div>
            </Card>

            {/* ── THE OPERATOR SURFACE (A20 1b) ───────────────────────────────
                Two cards, not one toolbar: moving the status arms a retention
                clock, and re-arming a customer-portal sync publishes the
                customer's own words to GitHub. Those are different decisions
                with different consequences and they are given different room. */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 16 }}>
              <StatusControl ticket={ticket} onChanged={reload} />
              <GithubCard ticket={ticket} onChanged={reload} />
              {/* A20 C5. Renders NOTHING at all for a report that did not come
                  from an on-prem install — which is most of them — so it costs
                  a browser-filed ticket no space and no explanation. It is not
                  wired to `reload`: the job's progress belongs to the install's
                  own check-in cycle, not to a triage click. */}
              <SupportBundleCard ticketId={ticket.id} />
            </div>

            <Card>
              <CardHead
                title="Session replay"
                sub={
                  ticket.capture_available
                    ? `${formatDuration(ticket.capture_duration_ms)} of session · ${formatBytes(ticket.recording_bytes)} · served through this server, never from storage`
                    : 'No recording was attached to this report.'
                }
                right={<MaskPill {...maskEvidence} />}
              />
              <div style={{ padding: '16px 18px' }}>
                {!ticket.capture_available ? (
                  <div style={{ padding: '24px 20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                    {/* A20 C2 — WHY, not just THAT.
                        Two of these cases are certain and one is a list of
                        maybes, and a triager who cannot tell them apart wastes
                        time chasing a recording that was never going to exist.
                        `tprm7.trust` ships no DOM recorder at all, and every
                        anonymous report is stripped of its capture server-side
                        (bug-report.routes.ts, POST /reports/anonymous) whatever
                        surface it came from. Say so definitively; keep the
                        best-effort list for the surfaces where it is the truth. */}
                    {ticket.surface_key === 'tprm7.trust' ? (
                      <>
                        The Trust Center does not record the screen. It is a public, unauthenticated surface, and
                        its reports are filed through the anonymous ingest path, which strips any capture on
                        arrival — so no replay was ever going to be attached here. The console and network extracts
                        above are what that surface sends, and they are complete.
                      </>
                    ) : ticket.source === 'anonymous' ? (
                      <>
                        This report was filed from an auth page with no session. The anonymous ingest path strips
                        the recording server-side — an unauthenticated origin cannot push bytes into our storage —
                        so a replay is unavailable by design, not by failure. The console and network extracts
                        above did survive.
                      </>
                    ) : (
                      <>
                        This report carries no recording. On this surface capture is best-effort: rrweb may have
                        been unavailable, the reporter may have had capture disabled, or the buffer may have
                        exceeded the upload ceiling. The written report above is unaffected.
                      </>
                    )}
                  </div>
                ) : replay.kind === 'idle' ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)', maxWidth: 620, lineHeight: 1.5 }}>
                      {/* The last thing read before the click, so it is the
                          SERVER's answer where there is one and an attributed
                          claim where there is not — never a flat assertion made
                          out of the reporter's checkbox. */}
                      {maskReading.tone === 'hazard'
                        ? 'The server found legible text in these bytes despite the redaction claim on this ticket. Load it only if you need to, and treat everything in it as customer data.'
                        : maskReading.tone === 'confirmed'
                          ? 'The server checked these bytes and found the sampled text masked. Attributes, titles and URLs are never masked by the recorder, so read those with care.'
                          : ticket.masked
                            ? 'Nothing has confirmed the redaction claim for these bytes, so open it expecting redaction but not relying on it.'
                            : 'The recording contains the text that was visible on the reporter’s screen. Load it only if you need to, and treat it as customer data.'}
                    </div>
                    <button onClick={loadReplay} style={{ ...btnSec, background: 'var(--accent)', color: '#fff', border: '1px solid var(--accent)' }}>
                      <Film size={14} /> Load replay
                    </button>
                  </div>
                ) : replay.kind === 'loading' ? (
                  <Skel height={420} borderRadius={8} />
                ) : replay.kind === 'absent' ? (
                  <div
                    role="status"
                    style={{ padding: '10px 14px', borderRadius: 'var(--radius-md)', background: '#FEF3C7', border: '1px solid #FDE68A', color: '#78350F', fontSize: 13, lineHeight: 1.5 }}
                  >
                    The ticket says a recording was stored, but the server has no bytes for it. Either the streaming
                    endpoint is not deployed on this server yet, or the object has been purged by retention.
                  </div>
                ) : replay.kind === 'error' ? (
                  <div style={{ display: 'grid', gap: 10, justifyItems: 'start' }}>
                    <div
                      role="alert"
                      style={{ padding: '10px 14px', borderRadius: 'var(--radius-md)', background: '#FEE2E2', border: '1px solid #FECACA', color: '#7F1D1D', fontSize: 13, lineHeight: 1.5 }}
                    >
                      Could not load the recording: {replay.message}
                    </div>
                    <button onClick={loadReplay} style={btnSec}><RefreshCw size={13} /> Try again</button>
                  </div>
                ) : (
                  <ReplayPlayer events={replay.events} banner={<MaskBanner {...maskEvidence} compact />} />
                )}
              </div>
            </Card>

            {/* The audit trail, below the replay and above the extracts: it is
                the answer to "who moved this and why", which is a question
                about the TICKET rather than about the session. */}
            <TicketTimeline ticketId={ticket.id} refreshKey={timelineKey} />

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16 }}>
              <Card>
                <CardHead title="Console" sub={`${consoleLines.length} line${consoleLines.length === 1 ? '' : 's'} captured`} />
                <div style={{ padding: consoleLines.length ? 0 : '20px 18px', maxHeight: 320, overflowY: 'auto' }}>
                  {consoleLines.length === 0 ? (
                    <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>No console output was captured.</span>
                  ) : consoleLines.map((c, i) => (
                    <div
                      key={i}
                      style={{
                        padding: '7px 18px', borderBottom: '1px solid var(--border)', fontFamily: 'var(--mono)',
                        fontSize: 11.5, lineHeight: 1.5, color: c.level === 'error' ? 'var(--status-expired)' : c.level === 'warn' ? '#C2410C' : 'var(--text-secondary)',
                        wordBreak: 'break-word',
                      }}
                    >
                      <span style={{ opacity: 0.65 }}>[{(c.level || 'log').toUpperCase()}]</span> {(c.payload || []).join(' ')}
                    </div>
                  ))}
                </div>
              </Card>

              <Card>
                <CardHead title="Network" sub={`${networkLines.length} request${networkLines.length === 1 ? '' : 's'} captured · paths only, never bodies`} />
                <div style={{ padding: networkLines.length ? 0 : '20px 18px', maxHeight: 320, overflowY: 'auto' }}>
                  {networkLines.length === 0 ? (
                    <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>No requests were captured.</span>
                  ) : networkLines.map((n, i) => {
                    const bad = n.failed || (n.status ?? 0) >= 400;
                    return (
                      <div
                        key={i}
                        style={{
                          padding: '7px 18px', borderBottom: '1px solid var(--border)', fontFamily: 'var(--mono)',
                          fontSize: 11.5, lineHeight: 1.5, color: bad ? 'var(--status-expired)' : 'var(--text-secondary)',
                          display: 'flex', gap: 8, justifyContent: 'space-between',
                        }}
                      >
                        <span style={{ wordBreak: 'break-all' }}>{n.method || 'GET'} {n.url}</span>
                        <span style={{ whiteSpace: 'nowrap' }}>{n.failed ? 'FAILED' : (n.status ?? '—')}</span>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </div>

            <Card>
              <CardHead title="Client" sub="What the reporter's browser looked like" />
              <div style={{ padding: '16px 18px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
                <Field label="Browser">
                  {client?.userAgent ? <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5 }}>{client.userAgent}</span> : dash}
                </Field>
                <Field label="Viewport">
                  {client?.viewport?.width ? `${client.viewport.width} × ${client.viewport.height}` : dash}
                </Field>
                <Field label="Time zone">{client?.timeZone || dash}</Field>
                <Field label="Theme">{client?.colorScheme || dash}</Field>
                <Field label="Reduced motion">
                  {client?.prefersReducedMotion == null ? dash : client.prefersReducedMotion ? 'Yes' : 'No'}
                </Field>
                <Field label="Verified">{ticket.verified ? 'Yes' : 'No — unverified submission'}</Field>
              </div>
            </Card>
          </div>
        )}
      </StateFrame>
    </div>
  );
}

export default TicketDetailPage;
